/**
 * Document permissioning that Durable Objects are not part of.
 *
 * Cloud ACL (`da/access-config.ts`) already decides from owner/grants/meta plus
 * `X-Authenticated-*` headers. This module is the same check against a
 * Postgres/memory row so Node self-host does not grow a second policy.
 */

import { Data, Effect } from 'effect';

import type { DocumentRecord } from './types.js';

export type PersistencePrincipal = {
  userId: string | null;
  orgId?: string | null;
  projectId?: string | null;
};

export function isDocumentOwner(
  record: Pick<DocumentRecord, 'ownerUserId'>,
  userId: string | null,
): boolean {
  return Boolean(userId) && record.ownerUserId === userId;
}

/**
 * Published public fallback — the same rule `authorizeDocumentRoute` uses when
 * the caller has no identity headers and `is_published` is true.
 */
export function canReadPublishedFallback(
  record: Pick<DocumentRecord, 'isPublished'>,
  principal: PersistencePrincipal,
): boolean {
  return record.isPublished && !principal.userId && !principal.orgId && !principal.projectId;
}

export function canReadDocument(
  record: Pick<DocumentRecord, 'ownerUserId' | 'isPublished'>,
  principal: PersistencePrincipal,
): boolean {
  if (isDocumentOwner(record, principal.userId)) return true;
  return canReadPublishedFallback(record, principal);
}

export function canManageDocument(
  record: Pick<DocumentRecord, 'ownerUserId'>,
  principal: PersistencePrincipal,
): boolean {
  return isDocumentOwner(record, principal.userId);
}

export type DocumentAccessAction = 'read' | 'manage';

/**
 * Redacted authorization failure. Only the attempted action travels with the
 * error — never the principal's user/org/project ids, the owner id, or any
 * document field.
 */
export class DocumentAccessDeniedError extends Data.TaggedError('DocumentAccessDeniedError')<{
  readonly action: DocumentAccessAction;
}> {}

function authorize(
  action: DocumentAccessAction,
  allowed: boolean,
): Effect.Effect<void, DocumentAccessDeniedError> {
  return allowed
    ? Effect.succeed(undefined)
    : Effect.fail(new DocumentAccessDeniedError({ action }));
}

/**
 * Native read authorization for the Effect persistence path: succeeds when the
 * same policy the boolean predicates encode allows the read, fails with the
 * redacted {@link DocumentAccessDeniedError} otherwise.
 */
export function authorizeDocumentReadEffect(
  record: Pick<DocumentRecord, 'ownerUserId' | 'isPublished'>,
  principal: PersistencePrincipal,
): Effect.Effect<void, DocumentAccessDeniedError> {
  return authorize('read', canReadDocument(record, principal));
}

/** Native manage (write/delete) authorization. Owner-only, same as the predicate. */
export function authorizeDocumentManageEffect(
  record: Pick<DocumentRecord, 'ownerUserId'>,
  principal: PersistencePrincipal,
): Effect.Effect<void, DocumentAccessDeniedError> {
  return authorize('manage', canManageDocument(record, principal));
}
